import { incApi } from 'api/inc-api';
import { DateTime } from 'luxon';
import { getClientId } from 'util/client-id';
import {
  generateInvalidates,
  generateProvides,
  generateProvidesList,
} from 'util/generateTags';
import { getMerchantId } from 'util/merchant-id';
import { getProgramId } from 'util/program-id';
import {
  Campaign,
  CreateCampaignArgs,
  CreateCampaignResult,
  CreateDraftOfferBody,
  GetDraftOfferResult,
  PublishDraftOfferResult,
  UpdateDraftOfferBody,
  UpdateOfferBody,
  GetOfferDistributionSettingsResult,
  GetOfferResult,
  ListCampaignResult,
  ListOffersResult,
  ListProgramsResult,
  OffersResult,
  OfferParamsType,
} from './offers-domain.types';
import { getOfferParams } from './offers-domain.utils';

const domain = 'incentivio-offers-domain';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    listCampaigns: build.query<
      ListCampaignResult,
      { count: number; page: number; title?: string }
    >({
      query: params => ({
        url: `${domain}/clients/merchants/campaigns`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.campaigns,
        element => element.campaignId,
        'Campaign',
      ),
    }),
    getCampaign: build.query<
      Campaign,
      { programId: string; campaignId: string }
    >({
      query: arg => {
        const { programId, campaignId } = arg;
        return {
          url: `${domain}/clients/${getClientId()}/merchants/${getMerchantId()}/programs/${programId}/campaigns/${campaignId}`,
          method: 'GET',
        };
      },
      providesTags: generateProvides(result => result.campaignId, 'Campaign'),
    }),
    createCampaigns: build.mutation<CreateCampaignResult, CreateCampaignArgs>({
      query: body => {
        return {
          url: `${domain}/clients/${getClientId()}/merchants/${getMerchantId()}/programs/${getProgramId()}/campaigns`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Campaigns'],
    }),
    deleteCampaign: build.mutation<any, Campaign>({
      query: body => ({
        url: `${domain}/clients/${getClientId()}/merchants/${getMerchantId()}/programs/${
          body.programId
        }/campaigns/${body.campaignId}`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['Campaigns'],
    }),
    updateCampaign: build.mutation<any, Campaign>({
      query: ({ programId, campaignId, ...rest }) => {
        return {
          url: `${domain}/clients/${getClientId()}/merchants/${getMerchantId()}/programs/${programId}/campaigns/${campaignId}`,
          method: 'PUT',
          body: rest,
        };
      },
      invalidatesTags: generateInvalidates(body => body.campaignId, 'Campaign'),
    }),

    listPrograms: build.query<
      ListProgramsResult,
      { count: number; page: number }
    >({
      query: (params = { count: 1000, page: 0 }) => ({
        url: `${domain}/clients/${getClientId()}/programs`,
        params: {
          ...params,
          merchantid: getMerchantId(),
        },
      }),
      providesTags: generateProvidesList(
        result => result?.programs,
        element => element.programId,
        'Program',
      ),
    }),

    listOffers: build.query<ListOffersResult, OfferParamsType>({
      query: params => {
        return {
          url: `${domain}/clients/${getClientId()}/offers`,
          params: getOfferParams(params),
        };
      },
      providesTags: generateProvidesList(
        result => result?.offers,
        element => element.offerId,
        'Offer',
      ),
    }),

    getOffer: build.query<GetOfferResult, string>({
      query: offerId => ({
        url: `${domain}/clients/${getClientId()}/offers/${offerId}`,
      }),
      providesTags: generateProvides(result => result.offerId, 'Offer'),
    }),

    deleteOffer: build.mutation<void, string>({
      query: offerId => ({
        url: `${domain}/clients/${getClientId()}/merchants/${getMerchantId()}/offers/${offerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Offers'],
    }),

    updateOffer: build.mutation<{ offerId: string }, UpdateOfferBody>({
      query: ({ offerId, ...rest }) => ({
        url: `${domain}/clients/${getClientId()}/merchants/${getMerchantId()}/offers2/${offerId}`,
        method: 'PUT',
        body: {
          ...rest,
          skipDuplicateValidations: true,
        },
      }),
      invalidatesTags: generateInvalidates(
        body => body.offerId,
        ['Offer', 'OfferDistributionSettings'],
      ),
    }),

    getOfferDistributionSettings: build.query<
      GetOfferDistributionSettingsResult,
      string
    >({
      query: offerId => ({
        url: `${domain}/clients/${getClientId()}/merchants/${getMerchantId()}/offers/${offerId}/distributionsettings`,
      }),
      providesTags: generateProvides(
        (_result, _error, arg) => arg,
        'OfferDistributionSettings',
      ),
    }),

    sendOffer: build.mutation<
      [OffersResult],
      {
        userId: string;
        offerId: string;
        ignoreBlackoutPeriod: boolean;
        ignoreDistributionRules: boolean;
        ignoreRedistributionMode: boolean;
      }
    >({
      query: ({
        userId,
        offerId,
        ignoreBlackoutPeriod,
        ignoreDistributionRules,
        ignoreRedistributionMode,
      }) => {
        return {
          url: `${domain}/clients/${getClientId()}/offers/${offerId}/manualdistribution`,
          method: 'POST',
          body: {
            userId: userId,
            ignoreBlackoutPeriod,
            ignoreDistributionRules,
            ignoreRedistributionMode,
            localDateTime: DateTime.local()
              .toUTC()
              .toFormat("yyyy-MM-dd'T'HH:mm:ss"),
          },
        };
      },
    }),

    removeCustomerOffer: build.mutation<
      any,
      { userId: string; offerId: string }
    >({
      query: ({ userId, offerId }) => {
        return {
          url: `${domain}/users/${userId}/offers/${offerId}/delete`,
          method: 'DELETE',
        };
      },
      invalidatesTags: generateInvalidates(
        body => body.offerId,
        'CustomerOffer',
      ),
    }),

    createDraftOffer: build.mutation<
      { draftOfferId: string },
      CreateDraftOfferBody
    >({
      query: body => ({
        url: `${domain}/draftoffers`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Offers', 'DraftOffers'],
    }),

    updateDraftOffer: build.mutation<
      { draftOfferId: string },
      UpdateDraftOfferBody
    >({
      query: ({ draftOfferId, ...rest }) => ({
        url: `${domain}/draftoffers/${draftOfferId}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: generateInvalidates(
        body => body.draftOfferId,
        ['DraftOffer', 'DraftOffers', 'Offers'],
      ),
    }),

    getDraftOffer: build.query<GetDraftOfferResult, string>({
      query: draftOfferId => ({
        url: `${domain}/draftoffers/${draftOfferId}`,
      }),
      providesTags: generateProvides(
        result => result.draftOfferId,
        'DraftOffer',
      ),
    }),

    deleteDraftOffer: build.mutation<void, string>({
      query: draftOfferId => ({
        url: `${domain}/draftoffers/${draftOfferId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DraftOffers', 'Offers'],
    }),

    publishDraftOffer: build.mutation<PublishDraftOfferResult, string>({
      query: draftOfferId => ({
        url: `${domain}/draftoffers/${draftOfferId}/publish`,
        method: 'POST',
      }),
      invalidatesTags: ['Offers', 'DraftOffers'],
    }),

    copyOfferAsDraft: build.mutation<{ draftOfferId: string }, string>({
      query: offerId => ({
        url: `${domain}/offers/${offerId}/copyasdraft`,
        method: 'POST',
      }),
      invalidatesTags: ['Offers', 'DraftOffers'],
    }),

    copyDraftOfferAsDraft: build.mutation<{ draftOfferId: string }, string>({
      query: draftOfferId => ({
        url: `${domain}/draftoffers/${draftOfferId}/copyasdraft`,
        method: 'POST',
      }),
      invalidatesTags: ['Offers', 'DraftOffers'],
    }),
  }),
});

export const {
  useListCampaignsQuery,
  useLazyListCampaignsQuery,
  useGetCampaignQuery,
  useCreateCampaignsMutation,
  useDeleteCampaignMutation,
  useUpdateCampaignMutation,
  useListProgramsQuery,
  useLazyListProgramsQuery,
  useListOffersQuery,
  useLazyListOffersQuery,
  useGetOfferQuery,
  useSendOfferMutation,
  useRemoveCustomerOfferMutation,
  useDeleteOfferMutation,
  useUpdateOfferMutation,
  useGetOfferDistributionSettingsQuery,
  useCreateDraftOfferMutation,
  useUpdateDraftOfferMutation,
  useGetDraftOfferQuery,
  useDeleteDraftOfferMutation,
  usePublishDraftOfferMutation,
  useCopyOfferAsDraftMutation,
  useCopyDraftOfferAsDraftMutation,
} = endpoints;
